import {
  ChevronRight as ChevronRightIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
} from '@mui/icons-material';
import { Alert, Box, Divider, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { ReactComponent as DeleteIcon } from '../../assets/images/delete-icon.svg';

import {
  CHANGE_VISIBILITY_WARNING,
  HIDE_INVENTORY_ITEM_CHECKBOX_HELP_TEXT,
  HIDE_INVENTORY_ITEM_LAST_PHYSICAL_WARNING,
  QUANTITY,
} from '../../constants/campaigns';
import { IS_HIDDEN_FOR_RECIPIENT } from '../../constants/inventories';
import { useCurrency } from '../../contexts/CurrencyProvider';
import { IChangeBucketItemPayload } from '../../types/bucket';
import { ICampaignItem } from '../../types/campaigns';
import { IOnDemandItem } from '../../types/inventories';
import { BoxDetailsSidebarModeEnum } from '../../types/shell';
import { getSKUOptionsTotalCount, isItemMsku, isItemOutOfStock } from '../../utils/inventories';
import { FCDetails, Price } from '../index';
import { ItemCountWithWarning, MskuNestedItem, QuantitySelector, VisibilityToggle } from '../_mui';
import {
  ItemAmountInfo,
  ItemBody,
  ItemContainer,
  ItemContentContainer,
  ItemControlsContainer,
  ItemFooter,
  ItemQuantityAndPrice,
  ItemTextName,
  LogoImage,
  LogoWrapper,
  QuantitySelectorErrorTextContainer,
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
} from './BoxDetailsItem.styles';

interface IProps {
  item: ICampaignItem | IOnDemandItem;
  mode?: BoxDetailsSidebarModeEnum;
  onChange?: (itemData: IChangeBucketItemPayload) => void;
  onRemove?: (item: ICampaignItem | IOnDemandItem) => void;
  canHide?: boolean;
  canRemove?: boolean;
  isDirectSend?: boolean;
}

const BoxDetailsItem: React.FC<IProps> = ({ item, mode, onChange, onRemove, canHide = true, isDirectSend }) => {
  const {
    item_id,
    count,
    quantity,
    name,
    image_url,
    type,
    sku_options,
    is_hidden_for_recipient,
    product_sku,
    fulfillment_center_id,
    preorder_allowed: isPreorderAllowed,
    low_stock_threshold: threshold,
  } = useMemo(() => item, [item]);

  const {
    palette: { text, action },
    typography,
  } = useTheme();
  const { getItemPrice } = useCurrency();

  const isMsku = useMemo(() => isItemMsku({ type }), [type]);

  const itemDisplayedPrice = useMemo(() => {
    return getItemPrice(item);
  }, [item, getItemPrice]);

  const isEditModeEnabled = useMemo(() => mode === BoxDetailsSidebarModeEnum.Edit, [mode]);

  const handleQuantityChange = useCallback(
    (value: number | undefined = 0) => {
      onChange?.({
        id: item_id,
        name: QUANTITY,
        value,
      });
    },
    [item_id, onChange],
  );

  const handleHideItem = useCallback(() => {
    onChange?.({
      id: item_id,
      value: !is_hidden_for_recipient,
      name: IS_HIDDEN_FOR_RECIPIENT,
    });
  }, [onChange, item_id, is_hidden_for_recipient]);

  const handleRemoveItem = useCallback(() => {
    onRemove?.(item);
  }, [onRemove, item]);

  const isOutOfStock = useMemo(
    () => isItemOutOfStock({ count, preorder_allowed: isPreorderAllowed, type, sku_options }),
    [count, isPreorderAllowed, type, sku_options],
  );

  const maxItemsCount = useMemo(() => {
    if (isMsku) {
      return getSKUOptionsTotalCount(sku_options);
    }

    return count;
  }, [count, isMsku, sku_options]);

  const mskuItemStatusIcon = useMemo(() => {
    const hasOutOfStock = sku_options?.some(isItemOutOfStock);
    return hasOutOfStock ? <WarningAmberOutlinedIcon color="warning" fontSize="small" /> : null;
  }, [sku_options]);

  const isItemCountAlertVisible = useMemo(() => {
    const hasQuantity = !!quantity;
    const hasCount = !!count && count > 0;

    return hasQuantity && hasCount && quantity > count && !isPreorderAllowed && isEditModeEnabled && !isMsku;
  }, [quantity, count, isPreorderAllowed, isEditModeEnabled, isMsku]);

  const itemLayoutControls = useMemo(() => {
    return (
      <>
        {!isMsku && !isDirectSend && (isEditModeEnabled || is_hidden_for_recipient) && (
          <Tooltip
            title={!canHide ? HIDE_INVENTORY_ITEM_LAST_PHYSICAL_WARNING : HIDE_INVENTORY_ITEM_CHECKBOX_HELP_TEXT}
            placement="top"
            arrow
          >
            <Box>
              <VisibilityToggle isVisible={!is_hidden_for_recipient} onChange={handleHideItem} isEditable={canHide} />
            </Box>
          </Tooltip>
        )}
        {isEditModeEnabled && (
          <Tooltip title={onRemove ? 'Delete item' : CHANGE_VISIBILITY_WARNING} placement="top" arrow>
            <span>
              <IconButton
                size="medium"
                onClick={handleRemoveItem}
                aria-label="delete item"
                disableRipple
                disabled={!onRemove}
                sx={{
                  padding: 0.625,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DeleteIcon style={{ fill: onRemove ? action.active : action.disabled }} />
                </Box>
              </IconButton>
            </span>
          </Tooltip>
        )}
      </>
    );
  }, [
    isMsku,
    isDirectSend,
    isEditModeEnabled,
    is_hidden_for_recipient,
    canHide,
    handleHideItem,
    handleRemoveItem,
    onRemove,
    action,
  ]);

  const hasTransparency = useMemo(
    () => is_hidden_for_recipient || (isOutOfStock && !isEditModeEnabled),
    [is_hidden_for_recipient, isOutOfStock, isEditModeEnabled],
  );

  const itemLayout = useMemo(
    () => (
      <ItemContainer>
        {isItemCountAlertVisible && (
          <Alert severity="error">
            Only {count} are in stock. Reduce the quantity to {count} or less.
          </Alert>
        )}
        <ItemBody>
          <LogoWrapper hasTransparency={hasTransparency}>
            <LogoImage component="img" src={image_url} alt={name} loading="lazy" />
          </LogoWrapper>
          <ItemContentContainer>
            <Box>
              <ItemTextName variant="body1" hasTransparency={hasTransparency}>
                {name}
              </ItemTextName>
              <Box sx={{ mt: 0.5 }}>
                <FCDetails
                  fulfillmentCenterId={fulfillment_center_id}
                  productSku={product_sku}
                  withLabel={true}
                  isHorizontal={true}
                  isDisabled={is_hidden_for_recipient}
                />
              </Box>
            </Box>
            <ItemFooter>
              <ItemAmountInfo>
                {!isMsku ? (
                  <ItemCountWithWarning
                    type={type}
                    count={count}
                    isPreorderAllowed={isPreorderAllowed}
                    threshold={threshold}
                  />
                ) : (
                  mskuItemStatusIcon
                )}
              </ItemAmountInfo>
              <ItemQuantityAndPrice isEditModeEnabled={isEditModeEnabled}>
                {!isEditModeEnabled ? (
                  <Typography
                    sx={{
                      lineHeight: 1.2,
                      color: hasTransparency ? text.disabled : text.primary,
                    }}
                  >
                    Qty: {quantity}
                  </Typography>
                ) : (
                  <Box sx={{ flexDirection: 'column' }}>
                    <QuantitySelector
                      value={quantity}
                      onChange={handleQuantityChange}
                      onDelete={onRemove ? handleRemoveItem : undefined}
                      maxValue={isPreorderAllowed ? undefined : maxItemsCount}
                      deleteDisabledTooltip={!onRemove ? CHANGE_VISIBILITY_WARNING : undefined}
                    >
                      {!isPreorderAllowed && maxItemsCount !== undefined ? (
                        <>
                          {quantity > maxItemsCount ? (
                            <QuantitySelectorErrorTextContainer>
                              <Typography variant="body2" color="error" sx={{ fontSize: 12, lineHeight: 1.6 }}>
                                Max quantity: {maxItemsCount}
                              </Typography>
                            </QuantitySelectorErrorTextContainer>
                          ) : null}
                        </>
                      ) : undefined}
                    </QuantitySelector>
                  </Box>
                )}
                <Price
                  value={itemDisplayedPrice}
                  sx={{
                    fontSize: 16,
                    fontWeight: typography.fontWeightMedium,
                    display: 'flex',
                    alignItems: 'center',
                    color: text.primary,
                  }}
                />
              </ItemQuantityAndPrice>
            </ItemFooter>
          </ItemContentContainer>
          <ItemControlsContainer>{itemLayoutControls}</ItemControlsContainer>
        </ItemBody>
      </ItemContainer>
    ),
    [
      isItemCountAlertVisible,
      count,
      hasTransparency,
      image_url,
      name,
      fulfillment_center_id,
      product_sku,
      isMsku,
      type,
      isPreorderAllowed,
      threshold,
      mskuItemStatusIcon,
      isEditModeEnabled,
      quantity,
      handleQuantityChange,
      handleRemoveItem,
      is_hidden_for_recipient,
      maxItemsCount,
      itemDisplayedPrice,
      itemLayoutControls,
      typography,
      text,
    ],
  );

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', px: 0, py: 3, position: 'relative' }}>
        {isMsku ? (
          <StyledAccordion square disableGutters>
            <StyledAccordionSummary data-action="msku_toggle" expandIcon={<ChevronRightIcon />}>
              {itemLayout}
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              {sku_options?.map((skuOption, i) => (
                <MskuNestedItem key={skuOption.item_id} item={skuOption} index={i} />
              ))}
            </StyledAccordionDetails>
          </StyledAccordion>
        ) : (
          <>
            <Box sx={{ display: 'flex' }}>{itemLayout}</Box>
          </>
        )}
      </Box>
      <Divider />
    </>
  );
};

export default BoxDetailsItem;
